/**
 * configurationManager.js - Configuration Save/Load System
 *
 * Manages:
 * - collectConfigurationState() - Gather all configuration data
 * - saveConfiguration() - Save config with screenshots to backend
 * - loadConfigurationFromUuid() - Load from backend
 * - applyConfigurationState() - Apply loaded config to scene
 * - initConfiguratorButtons() - Download/Share buttons
 */

import * as THREE from 'three';
import {FetchService} from "dc-services/fetch.service";
import {getNewFlashmessage} from 'cronus-theme/default/global/js/flashmessages';
import {generateNewModal, showModal, closeModal} from 'cronus-theme/default/global/js/modal';
import {hideCustomSelect} from 'cronus-theme/default/global/js/customSelect';
import * as state from './state.js';
import {
    scene,
    camera,
    renderer,
    controls,
    floor,
    grid,
    fadePlane,
    groupDecals,
    groupDecalSettings,
    sideDecals,
    decalSettings,
    lockedDecals,
    sideMats,
    sideMeshes,
    uploadedGraphics,
    graphicLayers,
    graphicLayerMap,
    lockedGroupDecals,
    animationPaused,
    setAnimationPaused
} from './state.js';
import {
    createGroupTriplanarDecal,
    createTriplanarDecal,
    updateAllMaterialsWithGraphicLayers
} from './decalManager.js';
import {
    getCamoTexture,
    applyGroupColor,
    applyGroupCamoTexture,
    applySideColor,
    applySideCamoTexture,
    resetGroupToOriginal,
    resetSideToOriginal
} from './materialManager.js';
import { requestRender, textureLoader } from './sceneManager.js';
import { getGlobals, getLabelText } from './utils.js';
import { buildUIFromMarkers } from './markerManager.js';
import { setModelRadioChecked } from './modelManager.js';

const GLOBALS = getGlobals();

// Detect iOS devices for special handling
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
              (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// ===== CONFIGURATION CHANGE TRACKING =====
// Tracks if user has modified colors or decals (NOT size changes)
let configurationChanged = false;

export function markConfigurationChanged() {
    configurationChanged = true;
}

export function hasConfigurationChanged() {
    return configurationChanged;
}

export function resetConfigurationChanged() {
    configurationChanged = false;
}

// Global accessor for HubSpot form integration
window._configurationChanged = false;
window._onConfigurationChanged = [];
window._markConfigurationChanged = function() {
    configurationChanged = true;
    window._configurationChanged = true;

    // Trigger all registered callbacks
    if (window._onConfigurationChanged && window._onConfigurationChanged.length > 0) {
        window._onConfigurationChanged.forEach((callback, index) => {
            try {
                callback();
            } catch (error) {
                console.error('[CONFIG] Error in callback', index, error);
            }
        });
    }
};

// ===== CONFIGURATION TITLE MAPPINGS =====
function getOptionTitle(section, optionKey, sideName = null) {
    // Basis-Titel-Mappings
    const titleMap = {
        // Meta
        'url': 'URL',

        // Model
        'size': 'Größe (Pfad)',
        'size_name': 'Größe',

        // Farben
        'group_color': 'Farbe',
        'color': sideName ? `Farbe ${getLabelText(section.split('_')[0], sideName)}` : 'Farbe',

        // Gruppen-Decal
        'group_decal_position_x': 'Logo Position X',
        'group_decal_position_y': 'Logo Position Y',
        'group_decal_position_z': 'Logo Position Z',
        'group_decal_uv_x': 'Logo UV X',
        'group_decal_uv_y': 'Logo UV Y',
        'group_decal_normal_x': 'Logo Normal X',
        'group_decal_normal_y': 'Logo Normal Y',
        'group_decal_normal_z': 'Logo Normal Z',
        'group_decal_rotation': 'Logo Rotation',
        'group_decal_scale': 'Logo Größe',
        'group_decal_file': 'Logo Datei',
        'group_decal_filename': 'Logo Dateiname',

        // Seiten-Decal
        'decal_position_x': sideName ? `Logo Position X (${getLabelText(section.split('_')[0], sideName)})` : 'Logo Position X',
        'decal_position_y': sideName ? `Logo Position Y (${getLabelText(section.split('_')[0], sideName)})` : 'Logo Position Y',
        'decal_position_z': sideName ? `Logo Position Z (${getLabelText(section.split('_')[0], sideName)})` : 'Logo Position Z',
        'decal_normal_x': sideName ? `Logo Normal X (${getLabelText(section.split('_')[0], sideName)})` : 'Logo Normal X',
        'decal_normal_y': sideName ? `Logo Normal Y (${getLabelText(section.split('_')[0], sideName)})` : 'Logo Normal Y',
        'decal_normal_z': sideName ? `Logo Normal Z (${getLabelText(section.split('_')[0], sideName)})` : 'Logo Normal Z',
        'decal_rotation': sideName ? `Logo Rotation (${getLabelText(section.split('_')[0], sideName)})` : 'Logo Rotation',
        'decal_scale': sideName ? `Logo Größe (${getLabelText(section.split('_')[0], sideName)})` : 'Logo Größe',
        'decal_file': sideName ? `Logo Datei (${getLabelText(section.split('_')[0], sideName)})` : 'Logo Datei',
        'decal_filename': sideName ? `Logo Dateiname (${getLabelText(section.split('_')[0], sideName)})` : 'Logo Dateiname',
        'decal_locked': sideName ? `Logo gesperrt (${getLabelText(section.split('_')[0], sideName)})` : 'Logo gesperrt',

        // Texture
        'texture_path': sideName ? `Textur (${getLabelText(section.split('_')[0], sideName)})` : 'Textur',

        // Grafiken
        'image_data': 'Grafik Bilddaten',
        'filename': 'Dateiname',
        'graphic_id': 'Grafik ID',
        'index': 'Index',
        'rotation': 'Rotation',
        'scale': 'Skalierung',
        'center_uv_x': 'Position UV X',
        'center_uv_y': 'Position UV Y',
        'size_uv_x': 'Größe UV X',
        'size_uv_y': 'Größe UV Y',
        'world_position_x': 'Welt Position X',
        'world_position_y': 'Welt Position Y',
        'world_position_z': 'Welt Position Z',

        // Bilder/Screenshots
        'view_front': 'Ansicht Vorne',
        'view_back': 'Ansicht Hinten',
        'view_left': 'Ansicht Links',
        'view_right': 'Ansicht Rechts',
        'view_top': 'Ansicht Oben',
        'view_three_quarter': 'Ansicht Dreiviertel'
    };

    return titleMap[optionKey] || optionKey;
}

// Hilfsfunktion zum Erstellen einer Konfigurationsline mit automatischem Titel
function createConfigLine(section, optionKey, optionValue, syncToBC, sideName = null) {
    return {
        section,
        optionKey,
        optionValue,
        optionTitle: getOptionTitle(section, optionKey, sideName),
        syncToBC
    };
}

// ===== COLLECT CONFIGURATION STATE =====
export function collectConfigurationState() {
    const lines = [];
    let lineIndex = 0;

    lines.push(createConfigLine('meta', 'url', window.location.href, 0));
    lineIndex++;

    const panelContainer = document.querySelector('.configuratorPanel');
    const selectedSizeInput = panelContainer?.querySelector('input[name="modelSize"]:checked');
    if (selectedSizeInput) {
        // Speichere den Pfad
        lines.push(createConfigLine('model', 'size', selectedSizeInput.value, 0));
        lineIndex++;

        // Speichere den lesbaren Namen (aus dem Label)
        const label = panelContainer.querySelector(`label[for="${selectedSizeInput.id}"]`) ||
                     selectedSizeInput.closest('label');
        const sizeName = label ? label.textContent.trim() : selectedSizeInput.value;
        lines.push(createConfigLine('model', 'size_name', sizeName, 1));
        lineIndex++;
    }

    const groups = window._configGroups;
    if (groups) {
        groups.forEach((groupData, groupName) => {
            if (groupData.color) {
                lines.push(createConfigLine(groupName, 'group_color', groupData.color, 1));
                lineIndex++;
            }

            const groupDecal = groupDecals.get(groupName);
            const groupSettings = groupDecalSettings.get(groupName);

            if (groupDecal && groupSettings) {
                if (groupDecal.userData?.lastHit?.point) {
                    const point = groupDecal.userData.lastHit.point;
                    lines.push(createConfigLine(groupName, 'group_decal_position_x', point.x.toString(), 0));
                    lines.push(createConfigLine(groupName, 'group_decal_position_y', point.y.toString(), 0));
                    lines.push(createConfigLine(groupName, 'group_decal_position_z', point.z.toString(), 0));
                    lineIndex += 3;
                }

                if (groupDecal.userData?.lastHit?.uv) {
                    const uv = groupDecal.userData.lastHit.uv;
                    lines.push(createConfigLine(groupName, 'group_decal_uv_x', uv.x.toString(), 0));
                    lines.push(createConfigLine(groupName, 'group_decal_uv_y', uv.y.toString(), 0));
                    lineIndex += 2;
                }

                if (groupDecal.userData?.lastHit?.normal) {
                    const normal = groupDecal.userData.lastHit.normal;
                    lines.push(createConfigLine(groupName, 'group_decal_normal_x', normal.x.toString(), 0));
                    lines.push(createConfigLine(groupName, 'group_decal_normal_y', normal.y.toString(), 0));
                    lines.push(createConfigLine(groupName, 'group_decal_normal_z', normal.z.toString(), 0));
                    lineIndex += 3;
                }

                if (groupSettings.rotation !== undefined) {
                    lines.push(createConfigLine(groupName, 'group_decal_rotation', groupSettings.rotation.toString(), 0));
                    lineIndex++;
                }

                if (groupSettings.scale !== undefined) {
                    lines.push(createConfigLine(groupName, 'group_decal_scale', groupSettings.scale.toString(), 0));
                    lineIndex++;
                }

                if (groupSettings.file) {
                    lines.push(createConfigLine(groupName, 'group_decal_file', groupSettings.file, 0));
                    lineIndex++;
                }

                if (groupSettings.filename) {
                    lines.push(createConfigLine(groupName, 'group_decal_filename', groupSettings.filename, 0));
                    lineIndex++;
                }
            }

            groupData.sides.forEach((sideData, sideName) => {
                const sectionName = `${groupName}_${sideName}`;

                if (sideData.color) {
                    lines.push(createConfigLine(sectionName, 'color', sideData.color, 1, sideName));
                    lineIndex++;
                }

                const decal = sideDecals.get(sideName);
                const settings = decalSettings.get(sideName);

                if (decal && settings) {
                    if (decal.userData?.group && decal.userData.group !== groupName) {
                    } else {
                        if (decal.userData?.lastHit?.point) {
                            const point = decal.userData.lastHit.point;
                            lines.push(createConfigLine(sectionName, 'decal_position_x', point.x.toString(), 0, sideName));
                            lines.push(createConfigLine(sectionName, 'decal_position_y', point.y.toString(), 0, sideName));
                            lines.push(createConfigLine(sectionName, 'decal_position_z', point.z.toString(), 0, sideName));
                            lineIndex += 3;
                        }

                        if (decal.userData?.lastHit?.normal) {
                            const normal = decal.userData.lastHit.normal;
                            lines.push(createConfigLine(sectionName, 'decal_normal_x', normal.x.toString(), 0, sideName));
                            lines.push(createConfigLine(sectionName, 'decal_normal_y', normal.y.toString(), 0, sideName));
                            lines.push(createConfigLine(sectionName, 'decal_normal_z', normal.z.toString(), 0, sideName));
                            lineIndex += 3;
                        }

                        if (settings.rotation !== undefined) {
                            lines.push(createConfigLine(sectionName, 'decal_rotation', settings.rotation.toString(), 0, sideName));
                            lineIndex++;
                        }

                        if (settings.scale !== undefined) {
                            lines.push(createConfigLine(sectionName, 'decal_scale', settings.scale.toString(), 0, sideName));
                            lineIndex++;
                        }

                        if (settings.file) {
                            lines.push(createConfigLine(sectionName, 'decal_file', settings.file, 0, sideName));
                            lineIndex++;
                        }

                        if (settings.filename) {
                            lines.push(createConfigLine(sectionName, 'decal_filename', settings.filename, 1, sideName));
                            lineIndex++;
                        }

                        if (lockedDecals.has(sideName)) {
                            lines.push(createConfigLine(sectionName, 'decal_locked', 'true', 0, sideName));
                            lineIndex++;
                        }
                    }
                }

                const mats = sideData.mats;
                if (mats) {
                    for (const mat of mats) {
                        if (mat.map && mat.userData.hasTextureOverride && mat.map.userData?.texturePath) {
                            lines.push(createConfigLine(sectionName, 'texture_path', mat.map.userData.texturePath, 0, sideName));
                            lineIndex++;
                            break;
                        }
                    }
                }
            });
        });
    }

    // Hochgeladene Grafiken hinzufügen
    if (uploadedGraphics && uploadedGraphics.length > 0) {
        uploadedGraphics.forEach((graphic, index) => {
            const graphicSection = `graphic_${index}`;
            const layerIndex = graphicLayerMap.get(graphic.id);
            const layer = layerIndex !== undefined ? graphicLayers[layerIndex] : null;

            // Grafik-Datei (Base64)
            if (graphic.imageUrl) {
                lines.push(createConfigLine(graphicSection, 'image_data', graphic.imageUrl, 0));
            }

            // Dateiname
            if (graphic.filename) {
                lines.push(createConfigLine(graphicSection, 'filename', graphic.filename, 1));
            }

            // Grafik-ID
            lines.push(createConfigLine(graphicSection, 'graphic_id', graphic.id, 0));

            // Index
            lines.push(createConfigLine(graphicSection, 'index', index.toString(), 0));

            // Rotation
            lines.push(createConfigLine(graphicSection, 'rotation', graphic.rotation.toString(), 0));

            // Scale
            lines.push(createConfigLine(graphicSection, 'scale', graphic.scale.toString(), 0));

            // Position auf dem Modell (falls vorhanden)
            if (layer) {
                if (layer.centerUV) {
                    lines.push(createConfigLine(graphicSection, 'center_uv_x', layer.centerUV.x.toString(), 0));
                    lines.push(createConfigLine(graphicSection, 'center_uv_y', layer.centerUV.y.toString(), 0));
                }

                if (layer.sizeUV) {
                    lines.push(createConfigLine(graphicSection, 'size_uv_x', layer.sizeUV.x.toString(), 0));
                    lines.push(createConfigLine(graphicSection, 'size_uv_y', layer.sizeUV.y.toString(), 0));
                }

                if (layer.worldPosition) {
                    lines.push(createConfigLine(graphicSection, 'world_position_x', layer.worldPosition.x.toString(), 0));
                    lines.push(createConfigLine(graphicSection, 'world_position_y', layer.worldPosition.y.toString(), 0));
                    lines.push(createConfigLine(graphicSection, 'world_position_z', layer.worldPosition.z.toString(), 0));
                }
            }
        });
    }

    lines.forEach(line => {
        if (line.syncToBC === undefined) {
            const userConfigurableKeys = ['size', 'color', 'group_color', 'filename'];
            line.syncToBC = userConfigurableKeys.includes(line.optionKey) ? 1 : 0;
        }
    });

    return lines;
}

// ===== BLOB TO BASE64 =====
function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// ===== CAPTURE VIEWPORT IMAGES =====
async function captureViewportImages(scale = 1) {
    if (!renderer || !camera || !controls || !scene) {
        return {};
    }

    const originalPosition = camera.position.clone();
    const originalTarget = controls.target.clone();
    const originalEnabled = controls.enabled;
    const wasAnimationPaused = animationPaused;

    // Store original renderer size
    const originalSize = new THREE.Vector2();
    renderer.getSize(originalSize);

    // Set fixed 3:2 aspect ratio (1200x800), scaled for high-resolution exports
    const screenshotWidth = 1200 * scale;
    const screenshotHeight = 800 * scale;
    renderer.setSize(screenshotWidth, screenshotHeight, false);

    // Update camera aspect ratio
    const originalAspect = camera.aspect;
    camera.aspect = screenshotWidth / screenshotHeight;
    camera.updateProjectionMatrix();

    setAnimationPaused(true);

    controls.enabled = false;

    // Hide all decal frames before capturing screenshots
    const decalFrames = [];
    scene.children.forEach(child => {
        if (child.name && child.name.startsWith('decal-frame-') && child.visible) {
            decalFrames.push(child);
            child.visible = false;
        }
    });

    const bbox = new THREE.Box3();
    scene.traverse((obj) => {
        if (obj.isMesh && obj.visible) {
            if (obj === grid || obj === floor || obj === fadePlane) {
                return;
            }
            const objBox = new THREE.Box3().setFromObject(obj);
            const objSize = objBox.getSize(new THREE.Vector3());
            if (objSize.x > 1000 || objSize.y > 1000 || objSize.z > 1000) {
                return;
            }
            bbox.expandByObject(obj);
        }
    });

    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    const fov = THREE.MathUtils.degToRad(camera.fov);
    // Zoom closer: Changed from 1.2 to 0.85
    const distance = (maxDim / (2 * Math.tan(fov / 2))) * 0.85;

    const screenshots = {};

    const captureView = async (name, position, lookAt) => {
        camera.position.copy(position);
        camera.lookAt(lookAt);

        camera.updateMatrixWorld(true);
        camera.updateProjectionMatrix();

        renderer.render(scene, camera);

        // Use lower quality (0.75) to reduce payload size for large configurations
        const dataUrl = renderer.domElement.toDataURL('image/jpeg', 0.75);
        screenshots[name] = dataUrl;
    };

    try {
        await captureView(
            'front',
            new THREE.Vector3(center.x, center.y, center.z + distance),
            center
        );

        await captureView(
            'back',
            new THREE.Vector3(center.x, center.y, center.z - distance),
            center
        );

        await captureView(
            'left',
            new THREE.Vector3(center.x - distance, center.y, center.z),
            center
        );

        await captureView(
            'right',
            new THREE.Vector3(center.x + distance, center.y, center.z),
            center
        );

        // Top view - camera directly above
        await captureView(
            'top',
            new THREE.Vector3(center.x, center.y + distance, center.z),
            center
        );

        // Three quarter view from lower angle
        const angle = Math.PI / 4;
        const heightFactor = 0.1;
        await captureView(
            'three_quarter',
            new THREE.Vector3(
                center.x + distance * Math.cos(angle),
                center.y + distance * heightFactor,
                center.z + distance * Math.sin(angle)
            ),
            center
        );

    } finally {
        // Restore decal frames visibility
        decalFrames.forEach(frame => {
            frame.visible = true;
        });

        // Restore original renderer size
        renderer.setSize(originalSize.x, originalSize.y, false);

        // Restore original camera aspect ratio
        camera.aspect = originalAspect;
        camera.updateProjectionMatrix();

        camera.position.copy(originalPosition);
        camera.lookAt(originalTarget);
        controls.target.copy(originalTarget);
        controls.enabled = originalEnabled;

        camera.updateMatrixWorld(true);
        camera.updateProjectionMatrix();
        controls.update();

        for (let i = 0; i < 3; i++) {
            renderer.render(scene, camera);
            await new Promise(resolve => setTimeout(resolve, 16));
        }

        setAnimationPaused(wasAnimationPaused);
    }

    return screenshots;
}

// ===== SAVE CONFIGURATION =====
// Helper function to get the model name from the page
function getModelName() {
    // Das Label der aktuell ausgewählten Größen-Option (z.B. "Expandable
    // Single"/"Expandable Double") enthält bereits den vollständigen,
    // aktuellen Modellnamen inkl. Größe und hat Vorrang vor
    // .itemcardEilaHeadline/.configuratorHeadline, die nur einmal beim Laden
    // der Seite befüllt werden (siehe PDF-Titel-Bug: Größe blieb nach einem
    // Wechsel z.B. Single -> Double im PDF-Titel und Dateinamen veraltet
    // stehen).
    //
    // Wichtig: Absichtlich die CSS-Pseudoklasse ":checked" verwenden statt die
    // JS-Property .checked auf jedem Radio einzeln zu lesen. initSizeSelector()
    // in modelManager.js setzt die Property des initial ausgewählten Radios per
    // Object.defineProperty(...) fest auf "true", um kein change-Event
    // auszulösen - dadurch bleibt .checked für genau dieses eine Element
    // dauerhaft "true", auch nachdem der Nutzer eine andere Größe gewählt hat.
    // Die Pseudoklasse ":checked" spiegelt dagegen immer den echten, aktuellen
    // Auswahlzustand wider.
    const panelContainer = document.querySelector('.configuratorPanel');
    const checkedSizeInput = panelContainer?.querySelector('input[name="modelSize"]:checked');
    if (checkedSizeInput) {
        const label = panelContainer.querySelector(`label[for="${checkedSizeInput.id}"]`) || checkedSizeInput.closest('label');
        const sizeName = label ? label.textContent.trim() : checkedSizeInput.value;
        if (sizeName) {
            return sizeName;
        }
    }

    // Try to get from itemcardEilaHeadline first (original model name)
    const itemcardHeadline = document.querySelector('.itemcardEilaHeadline');
    if (itemcardHeadline && itemcardHeadline.textContent.trim()) {
        return itemcardHeadline.textContent.trim();
    }

    // Fallback to configuratorHeadline
    const configuratorHeadline = document.querySelector('.configuratorHeadline');
    if (configuratorHeadline && configuratorHeadline.textContent.trim()) {
        return configuratorHeadline.textContent.trim();
    }

    // Final fallback
    return 'Konfigurator';
}

// Liest Formulardaten die vom HubSpot-Formular über window._pendingFormData übergeben werden
function getFormData() {
    if (window._pendingFormData) {
        const data = { ...window._pendingFormData };
        window._pendingFormData = null;
        return data;
    }
    return {};
}

export async function saveConfiguration(title, description, requestType = 'request', configurationName = '') {
    const lines = collectConfigurationState();

    // Screenshots generieren
    if (requestType === 'download' || requestType === 'save' || requestType === 'request') {
        // Alle Ansichten für PDF-Download, Speichern und Anfrage
        const screenshots = await captureViewportImages();

        if (screenshots.front) {
            lines.push(createConfigLine('images', 'view_front', screenshots.front, 0));
        }
        if (screenshots.back) {
            lines.push(createConfigLine('images', 'view_back', screenshots.back, 0));
        }
        if (screenshots.left) {
            lines.push(createConfigLine('images', 'view_left', screenshots.left, 0));
        }
        if (screenshots.right) {
            lines.push(createConfigLine('images', 'view_right', screenshots.right, 0));
        }
        if (screenshots.top) {
            lines.push(createConfigLine('images', 'view_top', screenshots.top, 0));
        }
        if (screenshots.three_quarter) {
            lines.push(createConfigLine('images', 'view_three_quarter', screenshots.three_quarter, 0));
        }
    } else if (requestType === 'share') {
        // Nur Dreiviertel-Ansicht für Share-Links
        const screenshots = await captureViewportImages();

        if (screenshots.three_quarter) {
            lines.push({
                section: 'view',
                optionKey: 'front',
                optionValue: screenshots.three_quarter,
                optionTitle: 'Konfigurationsbild',
                syncToBC: 0
            });
        }
    }

    const processedLines = [];
    for (const line of lines) {
        const processedLine = {
            section: line.section,
            optionKey: line.optionKey,
            optionValue: line.optionValue,
            optionTitle: line.optionTitle || line.optionKey,
            syncToBC: line.syncToBC !== undefined ? line.syncToBC : 0
        };

        if (line.optionValue instanceof Blob || line.optionValue instanceof File) {
            processedLine.optionValue = await blobToBase64(line.optionValue);
        }

        processedLines.push(processedLine);
    }

    // Formulardaten aus dem HubSpot-Formular lesen (wenn verfügbar)
    const formData = getFormData();

    const payload = {
        header: {
            title: title || '',
            description: description || '',
            itemDescription: getModelName(),
            configurationName: configurationName || '',
            requestType: requestType,
            currentUrl: window.location.href,
            ...formData
        },
        lines: processedLines
    };

    try {
        const result = await FetchService.post('/em/configuratorHandler/saveConfiguration', payload);
        return result;
    } catch (error) {
        throw error;
    }
}

// ===== LOAD CONFIGURATION =====
export async function loadConfiguration(uuid) {
    try {
        const result = await FetchService.post('/em/configuratorHandler/getConfiguration', {uuid: uuid});
        return result;
    } catch (error) {
        throw error;
    }
}

// Helper: Log minimal structure without Base64 data
function logMinimal(label, obj) {
    if (Array.isArray(obj)) {
        const summary = obj.map(item => {
            if (typeof item !== 'object') return item;
            const keys = {};
            for (let k in item) {
                const v = item[k];
                keys[k] = typeof v === 'string' && v.length > 50 ? `str(${v.length})` : v;
            }
            return keys;
        });
    } else if (typeof obj === 'object') {
        const keys = {};
        for (let k in obj) {
            const v = obj[k];
            keys[k] = typeof v === 'string' && v.length > 50 ? `str(${v.length})` :
                     Array.isArray(v) ? `arr(${v.length})` :
                     v instanceof Map ? `Map(${v.size})` :
                     typeof v === 'object' ? `obj(${Object.keys(v || {}).length})` : v;
        }
    }
}

// ===== APPLY CONFIGURATION STATE =====
export async function applyConfigurationState(configData) {
    logMinimal('[CFG] configData:', configData);

    // Support both API response formats: lines (POST) or lineArray (GET response)
    let lines = configData?.lines || configData?.lineArray;

    logMinimal('[CFG] lines:', lines);

    if (!lines || !Array.isArray(lines) || lines.length === 0) {
        logMinimal('[CFG] configData:', configData);
        return false;
    }

    // Normalize snake_case from API to camelCase
    lines = lines.map(line => ({
        section: line.section,
        optionKey: line.option_key || line.optionKey,
        optionValue: line.option_value || line.optionValue,
        syncToBC: line.sync_to_bc !== undefined ? line.sync_to_bc : line.syncToBC
    }));

    // Gruppiere lines nach section für einfacheren Zugriff
    const linesBySection = new Map();
    for (const line of lines) {
        if (!linesBySection.has(line.section)) {
            linesBySection.set(line.section, []);
        }
        linesBySection.get(line.section).push(line);
    }

    logMinimal('[CFG] linesBySection:', linesBySection);

    try {
        // 1. ZUERST Model Size wiederherstellen (BEVOR auf _configGroups gewartet wird)
        const panelContainer = document.querySelector('.configuratorPanel');
        const modelLines = linesBySection.get('model') || [];
        let modelSizeChanged = false;
        let modelLoadTriggered = false;

        for (const line of modelLines) {
            if (line.optionKey === 'size') {
                const sizeInput = panelContainer?.querySelector(`input[name="modelSize"][value="${line.optionValue}"]`);
                if (sizeInput) {
                    console.log('[CONFIG-LOAD] Found size input for model:', line.optionValue);
                    // CRITICAL: Set radio as checked BEFORE dispatching change event
                    // The change event handler checks if radio.checked is true
                    if (!sizeInput.checked) {
                        modelSizeChanged = true;
                        console.log('[CONFIG-LOAD] Setting radio button checked');
                        sizeInput.checked = true; // Direct assignment - must be synchronous
                    }

                    // Triggere change event um Model zu laden
                    // On iOS, delay the event dispatch slightly to ensure page is fully loaded
                    console.log('[CONFIG-LOAD] Dispatching change event to trigger model load');
                    if (isIOS) {
                        setTimeout(() => {
                            console.log('[CONFIG-LOAD] iOS: Dispatching delayed change event');
                            sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
                        }, 350);
                    } else {
                        sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    modelLoadTriggered = true;
                } else {
                    console.error('[CONFIG-LOAD] ❌ Size input not found for:', line.optionValue);
                }
            }
        }

        // Fallback: Wenn keine Model-Size in Config, lade erstes/default Model
        if (!modelLoadTriggered) {
            console.log('[CONFIG-LOAD] No model size in config, using fallback');
            const firstSizeInput = panelContainer?.querySelector('input[name="modelSize"]:checked')
                || panelContainer?.querySelector('input[name="modelSize"]');
            if (firstSizeInput) {
                console.log('[CONFIG-LOAD] Fallback: Found first size input:', firstSizeInput.value);
                // CRITICAL: Set radio as checked BEFORE dispatching change event
                if (!firstSizeInput.checked) {
                    console.log('[CONFIG-LOAD] Fallback: Setting radio checked');
                    firstSizeInput.checked = true; // Direct assignment - must be synchronous
                }
                // Dispatch change event
                console.log('[CONFIG-LOAD] Fallback: Dispatching change event');
                if (isIOS) {
                    setTimeout(() => {
                        console.log('[CONFIG-LOAD] iOS Fallback: Dispatching delayed change event');
                        firstSizeInput.dispatchEvent(new Event('change', { bubbles: true }));
                    }, 350);
                } else {
                    firstSizeInput.dispatchEvent(new Event('change', { bubbles: true }));
                }
                modelLoadTriggered = true;
            } else {
                console.error('[CONFIG-LOAD] ❌ Fallback: No size input found at all!');
            }
        }

        // Warte auf Model Load (entweder weil Size geändert wurde, oder initial Load)
        // Warte bis das Laden beginnt
        console.log('[CONFIG-LOAD] Waiting for model load to start...');
        let startWaitRetries = 0;
        while (!state.isLoading && startWaitRetries < 20) {
            await new Promise(resolve => setTimeout(resolve, 50));
            startWaitRetries++;
        }

        if (!state.isLoading && startWaitRetries >= 20) {
            console.error('[CONFIG-LOAD] ❌ Model load did not start! isLoading is still false');
        } else {
            console.log('[CONFIG-LOAD] Model load started, waiting for completion...');
        }

        // Warte bis das Laden abgeschlossen ist
        let loadWaitRetries = 0;
        const maxLoadWaitRetries = 200;
        while (state.isLoading && loadWaitRetries < maxLoadWaitRetries) {
            await new Promise(resolve => setTimeout(resolve, 100));
            loadWaitRetries++;
        }

        if (state.isLoading && loadWaitRetries >= maxLoadWaitRetries) {
            console.error('[CONFIG-LOAD] ❌ Model load timeout! Still loading after 20 seconds');
        } else {
            console.log('[CONFIG-LOAD] Model load completed');
        }

        // Zusätzliche Wartezeit für UI-Initialisierung und Meshes
        await new Promise(resolve => setTimeout(resolve, 800));

        // Warte bis _configGroups initialisiert wurde
        let groupsWaitRetries = 0;
        const maxGroupsWaitRetries = 50;
        while (!window._configGroups && groupsWaitRetries < maxGroupsWaitRetries) {
            await new Promise(resolve => setTimeout(resolve, 100));
            groupsWaitRetries++;
        }

        if (!window._configGroups) {
            console.error('[CFG] Timeout: _configGroups wurde nicht initialisiert');
            return false;
        }

        let groups = window._configGroups;
        window._initialModelLoaded = true;

        // 2. Farben wiederherstellen
        if (groups) {
            for (const [groupName, groupData] of groups.entries()) {
                // Gruppen-Farbe
                const groupLines = linesBySection.get(groupName) || [];
                const groupColorLine = groupLines.find(l => l.optionKey === 'group_color');
                if (groupColorLine && groupColorLine.optionValue) {
                    groupData.color = groupColorLine.optionValue;

                    // Prüfe ob es eine Camo-Textur ist
                    if (groupColorLine.optionValue === 'camo') {
                        applyGroupCamoTexture(groupName, textureLoader);
                    } else {
                        // Verwende die Standard-Funktion zum Färben
                        applyGroupColor(groupName, groupColorLine.optionValue);

                        // UI aktualisieren - Farbfeld im Panel aktualisieren
                        const groupRow = document.querySelector(`.group-color-row[data-group="${groupName}"]`);
                        if (groupRow) {
                            const colorSwatchDisplay = groupRow.querySelector('.color-swatch');
                            if (colorSwatchDisplay) {
                                colorSwatchDisplay.style.background = groupColorLine.optionValue;
                                colorSwatchDisplay.style.backgroundImage = '';
                            }
                        }

                        requestRender();
                    }
                }

                // Gruppen-Decals wiederherstellen
                const groupDecalFile = groupLines.find(l => l.optionKey === 'group_decal_file')?.optionValue;
                if (groupDecalFile) {
                    const pos = new THREE.Vector3(
                        parseFloat(groupLines.find(l => l.optionKey === 'group_decal_position_x')?.optionValue || 0),
                        parseFloat(groupLines.find(l => l.optionKey === 'group_decal_position_y')?.optionValue || 0),
                        parseFloat(groupLines.find(l => l.optionKey === 'group_decal_position_z')?.optionValue || 0)
                    );
                    const normal = new THREE.Vector3(
                        parseFloat(groupLines.find(l => l.optionKey === 'group_decal_normal_x')?.optionValue || 0),
                        parseFloat(groupLines.find(l => l.optionKey === 'group_decal_normal_y')?.optionValue || 0),
                        parseFloat(groupLines.find(l => l.optionKey === 'group_decal_normal_z')?.optionValue || 0)
                    );
                    const rotation = parseFloat(groupLines.find(l => l.optionKey === 'group_decal_rotation')?.optionValue || 0);
                    const scale = parseFloat(groupLines.find(l => l.optionKey === 'group_decal_scale')?.optionValue || 1);
                    const filename = groupLines.find(l => l.optionKey === 'group_decal_filename')?.optionValue;

                    // UV-Koordinaten (falls vorhanden)
                    const uvX = groupLines.find(l => l.optionKey === 'group_decal_uv_x')?.optionValue;
                    const uvY = groupLines.find(l => l.optionKey === 'group_decal_uv_y')?.optionValue;
                    const hitUV = (uvX && uvY) ? new THREE.Vector2(parseFloat(uvX), parseFloat(uvY)) : null;

                    // Texture aus Base64 laden
                    try {
                        const texture = await new Promise((resolve, reject) => {
                            textureLoader.load(groupDecalFile, resolve, undefined, reject);
                        });

                        // Gruppen-Decal platzieren
                        const decal = createGroupTriplanarDecal(groupName, pos, normal, texture, rotation, scale, hitUV);
                        if (decal) {
                            groupDecals.set(groupName, decal);
                            groupDecalSettings.set(groupName, {
                                rotation: rotation,
                                scale: scale,
                                file: groupDecalFile,
                                filename: filename
                            });
                        }
                    } catch (error) {
                        console.error(`Failed to load group decal for ${groupName}:`, error);
                    }
                }

                // Seiten-Farben
                for (const [sideName, sideData] of groupData.sides.entries()) {
                    const sectionName = `${groupName}_${sideName}`;
                    const sectionLines = linesBySection.get(sectionName) || [];
                    const colorLine = sectionLines.find(l => l.optionKey === 'color');

                    if (colorLine && colorLine.optionValue) {
                        // Speichere Farbe im Datenmodell
                        sideData.color = colorLine.optionValue;

                        // Prüfe ob es eine Camo-Textur ist
                        if (colorLine.optionValue === 'camo') {
                            applySideCamoTexture(groupName, sideName, textureLoader);
                        } else {
                            // Verwende die Standard-Funktion zum Färben
                            applySideColor(groupName, sideName, colorLine.optionValue);

                            // UI aktualisieren - Farbfeld im Panel aktualisieren
                            const sideRow = document.querySelector(`.side-color-row[data-side="${sideName}"]`);
                            if (sideRow) {
                                const colorSwatchDisplay = sideRow.querySelector('.color-swatch');
                                if (colorSwatchDisplay) {
                                    colorSwatchDisplay.style.background = colorLine.optionValue;
                                    colorSwatchDisplay.style.backgroundImage = '';
                                }
                            }

                            requestRender();
                        }

                        // Seiten-Decals wiederherstellen
                        const decalFile = sectionLines.find(l => l.optionKey === 'decal_file')?.optionValue;
                        if (decalFile) {
                            const pos = new THREE.Vector3(
                                parseFloat(sectionLines.find(l => l.optionKey === 'decal_position_x')?.optionValue || 0),
                                parseFloat(sectionLines.find(l => l.optionKey === 'decal_position_y')?.optionValue || 0),
                                parseFloat(sectionLines.find(l => l.optionKey === 'decal_position_z')?.optionValue || 0)
                            );
                            const normal = new THREE.Vector3(
                                parseFloat(sectionLines.find(l => l.optionKey === 'decal_normal_x')?.optionValue || 0),
                                parseFloat(sectionLines.find(l => l.optionKey === 'decal_normal_y')?.optionValue || 0),
                                parseFloat(sectionLines.find(l => l.optionKey === 'decal_normal_z')?.optionValue || 0)
                            );
                            const rotation = parseFloat(sectionLines.find(l => l.optionKey === 'decal_rotation')?.optionValue || 0);
                            const scale = parseFloat(sectionLines.find(l => l.optionKey === 'decal_scale')?.optionValue || 1);
                            const filename = sectionLines.find(l => l.optionKey === 'decal_filename')?.optionValue;

                            // Texture aus Base64 laden
                            try {
                                const texture = await new Promise((resolve, reject) => {
                                    textureLoader.load(decalFile, resolve, undefined, reject);
                                });

                                // Decal platzieren
                                const decal = createTriplanarDecal(groupName, sideName, pos, normal, texture, rotation, scale);
                                if (decal) {
                                    sideDecals.set(sideName, decal);
                                    decalSettings.set(sideName, {
                                        rotation: rotation,
                                        scale: scale,
                                        file: decalFile,
                                        filename: filename
                                    });
                                }
                            } catch (error) {
                                console.error(`Failed to load side decal for ${sectionName}:`, error);
                            }
                        }

                        // TODO: Texturen wiederherstellen
                        // Die texture_path Werte müssen geladen und auf die Materialien angewendet werden
                    }
                }
            }

            // 4. Hochgeladene Grafiken wiederherstellen
            const graphicSections = Array.from(linesBySection.keys()).filter(key => key.startsWith('graphic_'));

            if (graphicSections.length > 0) {
                for (const graphicSection of graphicSections) {
                    const graphicLines = linesBySection.get(graphicSection) || [];

                    const imageData = graphicLines.find(l => l.optionKey === 'image_data')?.optionValue;
                    const filename = graphicLines.find(l => l.optionKey === 'filename')?.optionValue;
                    const graphicId = graphicLines.find(l => l.optionKey === 'graphic_id')?.optionValue;
                    const index = parseInt(graphicLines.find(l => l.optionKey === 'index')?.optionValue || '0');
                    const rotation = parseFloat(graphicLines.find(l => l.optionKey === 'rotation')?.optionValue || '0');
                    const scale = parseFloat(graphicLines.find(l => l.optionKey === 'scale')?.optionValue || '1');

                    // Position auf dem Modell
                    const centerUV_x = graphicLines.find(l => l.optionKey === 'center_uv_x')?.optionValue;
                    const centerUV_y = graphicLines.find(l => l.optionKey === 'center_uv_y')?.optionValue;
                    const sizeUV_x = graphicLines.find(l => l.optionKey === 'size_uv_x')?.optionValue;
                    const sizeUV_y = graphicLines.find(l => l.optionKey === 'size_uv_y')?.optionValue;
                    const worldPos_x = graphicLines.find(l => l.optionKey === 'world_position_x')?.optionValue;
                    const worldPos_y = graphicLines.find(l => l.optionKey === 'world_position_y')?.optionValue;
                    const worldPos_z = graphicLines.find(l => l.optionKey === 'world_position_z')?.optionValue;

                    if (!imageData || !filename) {
                        continue;
                    }


                    try {
                        // Fake File object aus Base64 erstellen (ohne fetch wegen CSP)

                        // Validiere Base64 Daten
                        if (!imageData.includes(',')) {
                            throw new Error('Invalid imageData format - missing comma');
                        }

                        const parts = imageData.split(',');
                        if (parts.length < 2 || !parts[1]) {
                            throw new Error('Invalid imageData format - no base64 data');
                        }

                        let base64Data = parts[1].trim();

                        // Entferne alle nicht-Base64 Zeichen UND alle Padding-Zeichen
                        // Padding wird später korrekt neu hinzugefügt
                        base64Data = base64Data.replace(/[^A-Za-z0-9+/]/g, '');

                        if (base64Data.length === 0) {
                            throw new Error('Base64 data is empty');
                        }

                        const mimeType = imageData.match(/data:([^;]+);/)?.[1] || 'image/png';

                        // Erstelle ein minimales File-Objekt für Metadaten
                        // Das Texture wird später direkt aus imageData (data URL) geladen
                        const file = new File([], filename, {type: mimeType});

                        // Grafik zum uploadedGraphics Array hinzufügen
                        const graphic = {
                            id: graphicId || `graphic-restored-${Date.now()}-${index}`,
                            file: file,
                            filename: filename,
                            imageUrl: imageData,
                            index: index,
                            rotation: rotation,
                            scale: scale,
                            isEditing: false
                        };

                        uploadedGraphics.push(graphic);

                        // Grafik-Texture laden
                        // Konvertiere Data-URL zu Blob-URL um CSP und Browser-URL-Längenlimits zu umgehen
                        const blobUrl = await (async () => {
                            try {
                                // Extrahiere Base64 und MIME-Type aus der Data-URL
                                const parts = imageData.split(',');
                                if (parts.length < 2) return imageData;

                                const base64Data = parts[1];
                                const mimeMatch = imageData.match(/data:([^;]+);/);
                                const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';

                                // Konvertiere Base64 zu Blob ohne fetch (CSP-freundlich)
                                const byteCharacters = atob(base64Data);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], {type: mimeType});

                                // Erstelle Object-URL aus Blob
                                return URL.createObjectURL(blob);
                            } catch (err) {
                                // Fallback: Versuche es direkt mit der Data-URL
                                return imageData;
                            }
                        })();

                        const texture = await new Promise((resolve, reject) => {
                            textureLoader.load(blobUrl, (loadedTexture) => {
                                // Gebe die Blob-URL nach erfolgreichem Laden frei
                                if (blobUrl !== imageData) {
                                    URL.revokeObjectURL(blobUrl);
                                }
                                resolve(loadedTexture);
                            }, undefined, (error) => {
                                // Gebe die Blob-URL auch bei Fehler frei
                                if (blobUrl !== imageData) {
                                    URL.revokeObjectURL(blobUrl);
                                }
                                reject(error);
                            });
                        });

                        texture.colorSpace = THREE.SRGBColorSpace;
                        texture.needsUpdate = true;

                        // Finde einen freien Layer-Slot
                        let layerIndex = graphicLayers.findIndex(layer => layer === null);
                        if (layerIndex === -1 && uploadedGraphics.length <= 8) {
                            layerIndex = index; // Verwende den original Index falls verfügbar
                        }

                        if (layerIndex !== -1 && layerIndex < 8) {
                            graphicLayerMap.set(graphic.id, layerIndex);

                            // Grafik-Layer erstellen mit gespeicherten Daten
                            const img = texture.image;
                            const aspectRatio = img ? img.width / img.height : 1;

                            graphicLayers[layerIndex] = {
                                texture: texture,
                                centerUV: (centerUV_x && centerUV_y) ?
                                    new THREE.Vector2(parseFloat(centerUV_x), parseFloat(centerUV_y)) :
                                    new THREE.Vector2(0.5, 0.5),
                                sizeUV: (sizeUV_x && sizeUV_y) ?
                                    new THREE.Vector2(parseFloat(sizeUV_x), parseFloat(sizeUV_y)) :
                                    new THREE.Vector2(0.5 * aspectRatio * scale, 0.5 * scale),
                                rotation: THREE.MathUtils.degToRad(rotation),
                                graphicId: graphic.id,
                                worldPosition: (worldPos_x && worldPos_y && worldPos_z) ?
                                    new THREE.Vector3(parseFloat(worldPos_x), parseFloat(worldPos_y), parseFloat(worldPos_z)) :
                                    new THREE.Vector3()
                                // hitMesh wird später gefunden wenn das Decal editiert wird
                            };

                            // Settings speichern
                            groupDecalSettings.set(graphic.id, {
                                rotation: rotation,
                                scale: scale,
                                file: imageData,
                                filename: filename
                            });

                            // Grafik als gesperrt markieren
                            lockedGroupDecals.set(graphic.id, true);
                        }

                    } catch (error) {
                        console.error('Failed to restore graphic:', filename, error);
                    }
                }

                // Alle Materialien mit den wiederhergestellten Grafik-Layern aktualisieren
                if (uploadedGraphics.length > 0) {
                    updateAllMaterialsWithGraphicLayers();
                }
            }

            // UI mit den korrekten Farben neu aufbauen
            buildUIFromMarkers(groups);

            // Event-Listener für Size-Selector neu initialisieren (nach buildUIFromMarkers)
            // weil cloneNode die Event-Listener nicht kopiert
            if (typeof window._reloadModel === 'function') {
                const { initSizeSelector } = await import('./modelManager.js');
                initSizeSelector(window._reloadModel, true); // skipInitialLoad = true
            }

            // UI aktualisieren: Grafiken im Panel anzeigen (NACH buildUIFromMarkers)
            if (uploadedGraphics.length > 0 && typeof window._addGraphicToList === 'function') {
                uploadedGraphics.forEach(graphic => {
                    // Prüfe ob die Grafik bereits im DOM existiert
                    const existingRow = document.querySelector(`.graphic-row[data-graphic-id="${graphic.id}"]`);
                    if (!existingRow) {
                        window._addGraphicToList(graphic);
                    }

                    // UI aktualisieren: Hint verstecken, Sliders anzeigen (da bereits platziert)
                    if (window._graphicUIUpdaters && window._graphicUIUpdaters.has(graphic.id)) {
                        window._graphicUIUpdaters.get(graphic.id)();
                    }
                });
            }

            requestRender();
            return true;
        }
        } catch (error) {
            console.error('Configuration restoration failed:', error);
            return false;
        }
    }


function debugConfiguration() {
    const lines = collectConfigurationState();
    return lines;
}

// ===== DOWNLOAD: PDF =====
async function downloadConfigurationPdf() {
    try {
        const result = await saveConfiguration(
            null, // Use model name from page
            '',
            'download'
        );

        if (result && result.success) {
            // API gibt die UUID zurück, mit der wir den Download starten können
            const uuid = result.uuid || result.data?.uuid;
            const downloadUrl = result.data?.fullDownloadPdfPath;
            const downloadFilename = result.data?.downloadPdf || `konfigurator-${uuid || 'export'}.pdf`;

            // Download starten
            if (downloadUrl && downloadUrl !== '') {
                // PDF direkt herunterladen statt in neuem Tab öffnen
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = downloadFilename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } else {
                alert('Download-URL konnte nicht generiert werden.');
            }
        } else {
            console.error('[DOWNLOAD] API Error:', result);
            alert(result?.message || 'Download konnte nicht erstellt werden.');
        }
    } catch (error) {
        alert('Fehler beim Erstellen des Downloads. Bitte versuchen Sie es erneut.');
    }
}

// ===== DOWNLOAD: HIGH-RES IMAGES =====
async function downloadHighResImages(scale) {
    // Nutzt die UUID der bereits geladenen/gespeicherten Konfiguration aus der URL,
    // speichert sonst - wie beim PDF-Download - automatisch eine neue Konfiguration.
    let uuid = new URLSearchParams(window.location.search).get('uuid');

    if (!uuid) {
        const saveResult = await saveConfiguration(null, '', 'download');

        if (!saveResult || !saveResult.success) {
            console.error('[DOWNLOAD IMAGES] Save error:', saveResult);
            getNewFlashmessage(
                GLOBALS.tc.download_images_error || 'Fehler beim Erstellen des Bilder-Downloads. Bitte versuchen Sie es erneut.',
                3
            );
            return;
        }

        uuid = saveResult.uuid || saveResult.data?.uuid;

        if (!uuid) {
            getNewFlashmessage(
                GLOBALS.tc.download_images_error || 'Fehler beim Erstellen des Bilder-Downloads. Bitte versuchen Sie es erneut.',
                3
            );
            return;
        }

        const newParams = new URLSearchParams(window.location.search);
        newParams.set('uuid', uuid);
        window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
    }

    try {
        const images = await captureViewportImages(scale);
        const result = await FetchService.post('/em/configuratorHandler/downloadConfigurationImages', {
            uuid,
            images
        });

        if (result && result.success) {
            const downloadUrl = result.data?.fullDownloadZipPath;
            const downloadFilename = result.data?.downloadZip || `konfigurator-bilder-${uuid}.zip`;

            if (downloadUrl) {
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = downloadFilename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } else {
                getNewFlashmessage(
                    GLOBALS.tc.download_images_error || 'Fehler beim Erstellen des Bilder-Downloads. Bitte versuchen Sie es erneut.',
                    3
                );
            }
        } else {
            console.error('[DOWNLOAD IMAGES] API Error:', result);
            getNewFlashmessage(
                GLOBALS.tc.download_images_error || 'Fehler beim Erstellen des Bilder-Downloads. Bitte versuchen Sie es erneut.',
                3
            );
        }
    } catch (error) {
        console.error('[DOWNLOAD IMAGES] Error:', error);
        getNewFlashmessage(
            GLOBALS.tc.download_images_error || 'Fehler beim Erstellen des Bilder-Downloads. Bitte versuchen Sie es erneut.',
            3
        );
    }
}

// ===== DOWNLOAD OPTIONS MODAL =====
function openDownloadOptionsModal() {
    const modal = generateNewModal('downloadOptionsModalTemplate');
    if (!modal) {
        return;
    }

    const scaleWrapper = modal.querySelector('.downloadOptionsScale');
    const scaleSelect = modal.querySelector('#downloadOptionsScaleSelect');
    const confirmBtn = modal.querySelector('.js-downloadOptionsConfirm');
    const typeInputs = modal.querySelectorAll('input[name="downloadOptionType"]');

    // Klick-Verdrahtung fuer das Scale-customSelect bewusst hier von Hand statt
    // ueber initCustomSelects() (siehe #229871 im Portal): die Funktion haengt
    // bei jedem Aufruf einen weiteren globalen document-Click-Listener an -
    // bei einem Modal, das bei jedem Oeffnen neu generiert wird, sammeln sich
    // die Listener und schalten .is-active mehrfach pro Klick um.
    const scaleCustomSelect = modal.querySelector('.downloadOptionsScale .js-customSelect');
    if (scaleCustomSelect) {
        const scaleTrigger = scaleCustomSelect.querySelector('.customSelect__selected');
        for (const item of scaleCustomSelect.querySelectorAll('.customSelect__dropdownItem')) {
            item.addEventListener('click', () => {
                scaleSelect.value = item.dataset.value || scaleSelect.value;
                if (scaleTrigger) {
                    const headline = item.querySelector('.customSelect__dropdownItemHeadline');
                    scaleTrigger.innerHTML = headline ? headline.innerHTML : item.textContent;
                }
                for (const other of scaleCustomSelect.querySelectorAll('.customSelect__dropdownItem')) {
                    other.classList.toggle('is-active', other === item);
                }
                hideCustomSelect(scaleCustomSelect);
            });
        }
    }

    typeInputs.forEach((input) => {
        input.addEventListener('change', () => {
            const selectedType = modal.querySelector('input[name="downloadOptionType"]:checked')?.value;
            scaleWrapper.hidden = selectedType !== 'images';
        });
    });

    confirmBtn.addEventListener('click', async () => {
        const selectedType = modal.querySelector('input[name="downloadOptionType"]:checked')?.value || 'pdf';

        confirmBtn.classList.add('is-loading');
        try {
            if (selectedType === 'images') {
                const scale = parseInt(scaleSelect.value, 10) || 1;
                await downloadHighResImages(scale);
            } else {
                await downloadConfigurationPdf();
            }
            closeModal(modal);
        } finally {
            confirmBtn.classList.remove('is-loading');
        }
    });

    showModal(modal);
}

// ===== INIT CONFIGURATOR BUTTONS =====
// ===== SAVE CONFIGURATION (mit optionalem Konfigurationsnamen) =====
async function performSaveConfiguration(configurationName) {
    // Kundendaten aus dem DOM lesen
    const customerDataEl = document.getElementById('customerData');
    let customerData = {};

    if (customerDataEl && customerDataEl.dataset.json) {
        try {
            customerData = JSON.parse(customerDataEl.dataset.json);
        } catch (parseError) {
            console.error('Error parsing customerData JSON:', parseError);
        }
    }

    // Wenn wir im Portal sind (customerData vorhanden), setzen wir die Daten für saveConfiguration
    if (customerData && Object.keys(customerData).length > 0) {
        // Die saveConfiguration Funktion nutzt getFormData()
        // Wir setzen window._pendingFormData, damit saveConfiguration es aufgreift
        window._pendingFormData = {
            customer_no: customerData.customer_no,
            company_name: customerData.company_name,
            firstName: customerData.first_name,
            lastName: customerData.last_name,
            eMail: customerData.mail
        };
    }

    // Konfiguration speichern
    const result = await saveConfiguration(
        customerData.company_name || null,
        `Konfiguration für ${customerData.first_name || ''} ${customerData.last_name || ''}`.trim(),
        'save',
        configurationName || ''
    );

    if (result && result.success) {
        getNewFlashmessage(
            GLOBALS.tc.configuration_saved || 'Konfiguration wurde erfolgreich gespeichert',
            'success'
        );
    } else {
        console.error('[SAVE] API Error:', result);
        alert(result?.message || 'Speichern fehlgeschlagen.');
    }
}

// ===== SAVE MODAL =====
function openSaveConfigurationModal() {
    const modal = generateNewModal('saveConfigurationModalTemplate');
    if (!modal) {
        return;
    }

    const nameInput = modal.querySelector('.js-saveConfigurationNameInput');
    const confirmBtn = modal.querySelector('.js-saveConfigurationConfirm');

    if (nameInput) {
        nameInput.value = getModelName();
    }

    if (confirmBtn) {
        confirmBtn.addEventListener('click', async () => {
            confirmBtn.classList.add('is-loading');
            try {
                await performSaveConfiguration(nameInput ? nameInput.value.trim() : '');
                closeModal(modal);
            } catch (error) {
                console.error('[SAVE] Error:', error);
                alert('Fehler beim Speichern. Bitte versuchen Sie es erneut.');
            } finally {
                confirmBtn.classList.remove('is-loading');
            }
        });
    }

    showModal(modal);
}

export function initConfiguratorButtons() {
    const downloadBtn = document.querySelector('.configuratorButtonDownload');
    const saveBtn = document.querySelector('.configuratorButtonSave');
    const shareBtn = document.querySelector('.configuratorButtonShare');

    if (downloadBtn) {
        downloadBtn.addEventListener('click', (e) => {
            e.preventDefault();
            openDownloadOptionsModal();
        });
    }

    if (saveBtn) {
        saveBtn.addEventListener('click', (e) => {
            e.preventDefault();
            openSaveConfigurationModal();
        });
    }

    if (shareBtn) {
        shareBtn.addEventListener('click', async (e) => {
            e.preventDefault();

            shareBtn.classList.add('is-loading');

            try {
                const result = await saveConfiguration(
                    null, // Use model name from page
                    '',
                    'share'
                );

                if (result && result.success) {
                    const uuid = result.uuid || result.data?.uuid;

                    // Konstruiere die Share-URL IMMER selbst (ignoriere Server-URL für korrekte Formatierung)
                    let shareUrl = null;
                    if (uuid) {
                        // Hole nur die saubere Base-URL (Origin + Pathname, OHNE Query-String)
                        const baseUrl = window.location.origin + window.location.pathname;

                        // Erstelle komplett neue Parameter (keine alten übernehmen um Fehler zu vermeiden)
                        const cleanParams = new URLSearchParams();

                        // Füge nur die UUID hinzu (alle anderen Parameter ignorieren)
                        cleanParams.set('uuid', uuid);

                        // Baue finale URL
                        shareUrl = `${baseUrl}?${cleanParams.toString()}`;
                    }

                    const title = GLOBALS.tc.my_configuration || 'Meine Konfiguration';

                    // Versuche Web Share API
                    let shareSuccessful = false;
                    if (navigator.share && navigator.canShare) {
                        try {
                            await navigator.share({
                                title: title,
                                url: shareUrl
                            });
                            shareSuccessful = true;
                        } catch (shareError) {
                            // User hat abgebrochen oder Browser unterstützt es nicht
                            // Fallback wird unten ausgeführt
                        }
                    }

                    // Fallback: Kopiere in Zwischenablage
                    if (!shareSuccessful) {
                        try {
                            await navigator.clipboard.writeText(shareUrl);
                            // Zeige Flash-Message
                            getNewFlashmessage(
                                GLOBALS.tc.link_copied_to_clipboard || 'Link wurde in die Zwischenablage kopiert'
                            );
                        } catch (clipboardError) {
                            // Fallback für ältere Browser
                            const textArea = document.createElement('textarea');
                            textArea.value = shareUrl;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-999999px';
                            document.body.appendChild(textArea);
                            textArea.select();
                            try {
                                document.execCommand('copy');
                                getNewFlashmessage(
                                    GLOBALS.tc.link_copied_to_clipboard || 'Link wurde in die Zwischenablage kopiert'
                                );
                            } catch (err) {
                                alert('Kopieren fehlgeschlagen: ' + shareUrl);
                            }
                            document.body.removeChild(textArea);
                        }
                    }
                } else {
                    alert(result?.message || 'Share-Link konnte nicht erstellt werden.');
                }
            } catch (error) {
                alert('Fehler beim Erstellen des Share-Links. Bitte versuchen Sie es erneut.');
            } finally {
                shareBtn.classList.remove('is-loading');
            }
        });
    }

    setupRequestForm();
}

// ===== CHECK AND LOAD CONFIG FROM URL =====
export async function checkAndLoadConfigFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);

    // Hole alle uuid Parameter (falls mehrere vorhanden)
    const allUuids = urlParams.getAll('uuid');

    // Wenn keine UUID vorhanden, nichts tun
    if (allUuids.length === 0) {
        return;
    }

    // Nimm die letzte/neueste UUID (die zuletzt in der URL steht)
    const uuid = allUuids[allUuids.length - 1];

    // Scrolle sofort zum Konfigurator, noch bevor die Konfiguration geladen wird
    const configuratorElement = document.querySelector('#configurator');
    if (configuratorElement) {
        configuratorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Wenn mehrere UUIDs vorhanden waren, URL bereinigen
    if (allUuids.length > 1) {
        // Erstelle neue URL mit nur der neuesten UUID
        const newUrl = new URL(window.location.href);
        const newParams = new URLSearchParams();

        // Kopiere alle anderen Parameter (nicht uuid)
        for (const [key, value] of urlParams.entries()) {
            if (key !== 'uuid') {
                newParams.append(key, value);
            }
        }

        // Füge nur die neueste UUID hinzu
        newParams.set('uuid', uuid);

        // Update URL ohne Seite neu zu laden
        newUrl.search = newParams.toString();
        window.history.replaceState({}, '', newUrl.toString());
    }

    // Lade Konfiguration mit der neuesten UUID
    try {
        const result = await loadConfiguration(uuid);

        logMinimal('[CFG] result:', result);
        logMinimal('[CFG] result.configuration:', result?.configuration);
        logMinimal('[CFG] result.data?.configuration:', result?.data?.configuration);

        // Support both response formats: result.configuration or result.data.configuration
        const configuration = result?.configuration || result?.data?.configuration;

        if (result && result.success && configuration) {
            const applied = await applyConfigurationState(configuration);
        } else {
            console.error('[CONFIG-LOAD] ❌ Invalid configuration response:', result);
            // Load default model as fallback
            if (typeof window._loadDefaultModel === 'function') {
                window._loadDefaultModel();
            }
        }
    } catch (error) {
        console.error('[CONFIG-LOAD] ❌ Exception:', error);
        // Load default model as fallback
        if (typeof window._loadDefaultModel === 'function') {
            window._loadDefaultModel();
        }
    }
}

// Kopiere itemcardEilaHeadline Inhalt in configuratorHeadline
export function initConfiguratorHeadline() {
    const configuratorPanel = document.querySelector('.configuratorPanel');
    if (!configuratorPanel) return; // Kein Konfigurator vorhanden

    const itemcardHeadline = document.querySelector('.itemcardEilaHeadline');
    if (!itemcardHeadline) return; // Keine itemcardEilaHeadline vorhanden

    // Finde configuratorHeadline
    const configuratorHeadline = document.querySelector('.configuratorHeadline');
    if (!configuratorHeadline) return; // Keine configuratorHeadline vorhanden

    // Kopiere den Modellnamen von itemcardEilaHeadline
    const modelName = itemcardHeadline.textContent.trim();

    // Behalte den bestehenden Text und füge Modellname DAVOR ein
    const existingText = configuratorHeadline.innerHTML;

    // Setze Modellname VOR dem bestehenden Text
    configuratorHeadline.innerHTML = modelName + ' ' + existingText;
}

// Export debugging functions
window._saveConfiguration = saveConfiguration;
window._loadConfiguration = loadConfiguration;
window._applyConfigurationState = applyConfigurationState;
window._collectConfigurationState = collectConfigurationState;
window._debugConfiguration = debugConfiguration;

// Global function for HubSpot form integration - returns configuration URL
window._getConfigurationLink = async function() {
    try {
        const result = await saveConfiguration(
            null, // Use model name from page
            '',
            'share'
        );

        if (result && result.success) {
            const uuid = result.uuid || result.data?.uuid;
            if (uuid) {
                const baseUrl = window.location.origin + window.location.pathname;
                return `${baseUrl}?uuid=${uuid}`;
            }
        }
        return null;
    } catch (error) {
        console.error('[CONFIG] Error getting configuration link:', error);
        return null;
    }
};

// ===== HANDLE REQUEST FORM SUBMISSION =====
export function setupRequestForm() {
    // "Entwurf anfragen" has its own class since the AI Vision Studio button is primary as well (#233679)
    const configuratorButton = document.querySelector('.configuratorRequestPanel .configuratorButtonRequest')
        || document.querySelector('.configuratorRequestPanel .button--primary:not(.configuratorButtonAiVision)');

    if (!configuratorButton) return;

    configuratorButton.addEventListener('click', async (e) => {
        e.preventDefault();

        try {
            const result = await saveConfiguration(
                null, // Use model name from page
                '',
                'request'
            );

            if (result && result.success) {
                const uuid = result.uuid || result.data?.uuid;

                if (!uuid) {
                    throw new Error('Keine UUID erhalten');
                }

                const baseUrl = window.location.origin + window.location.pathname;
                const configUrl = `${baseUrl}?uuid=${uuid}`;

                window._configurationUuid = uuid;
                window._configurationUrl = configUrl;

                const requestForm = document.querySelector('form[name="requestForm"], form.requestForm, .modal form, form');

                const observer = new MutationObserver((mutations, obs) => {
                    const form = document.querySelector('form[name="requestForm"], form.requestForm, .modal form');
                    if (form) {
                        obs.disconnect();
                        injectConfigIntoForm(form, uuid, configUrl);
                        setupFormSubmitHandler(form);
                    }
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });

                setTimeout(() => observer.disconnect(), 5000);

                if (requestForm) {
                    injectConfigIntoForm(requestForm, uuid, configUrl);
                    setupFormSubmitHandler(requestForm);
                }

                if (configuratorButton.href) {
                    window.location.href = configuratorButton.href;
                } else if (configuratorButton.onclick) {
                    configuratorButton.onclick();
                }
            } else {
                alert(result?.message || 'Konfiguration konnte nicht gespeichert werden.');
            }
        } catch (error) {
            console.error('[REQUEST] Error:', error);
            alert('Fehler beim Speichern der Konfiguration. Bitte versuchen Sie es erneut.');
        }
    });
}

function injectConfigIntoForm(form, uuid, configUrl) {
    let uuidInput = form.querySelector('input[name="configuration_uuid"]');
    if (!uuidInput) {
        uuidInput = document.createElement('input');
        uuidInput.type = 'hidden';
        uuidInput.name = 'configuration_uuid';
        form.appendChild(uuidInput);
    }
    uuidInput.value = uuid;

    let urlInput = form.querySelector('input[name="configuration_url"]');
    if (!urlInput) {
        urlInput = document.createElement('input');
        urlInput.type = 'hidden';
        urlInput.name = 'configuration_url';
        form.appendChild(urlInput);
    }
    urlInput.value = configUrl;
}

function setupFormSubmitHandler(form) {
    if (form._configHandlerAttached) return;
    form._configHandlerAttached = true;

    form.addEventListener('submit', async (e) => {
        const submitButton = form.querySelector('button[type="submit"], input[type="submit"], .button--primary');

        if (submitButton) {
            submitButton.classList.add('is-loading');
            submitButton.disabled = true;
        }

        if (window._configurationUuid && window._configurationUrl) {
            injectConfigIntoForm(form, window._configurationUuid, window._configurationUrl);
        }
    });
}
